
/**
 * Normaliza distintos formatos de entrada geoespacial a un
 * GeoJSON FeatureCollection.
 *
 * Formatos soportados:
 *
 * - GeoJSON string
 *   '{"type":"FeatureCollection", ...}'
 *
 * - L.GeoJSON
 *   layer.toGeoJSON()
 *
 * - Objetos con propiedad geojson
 *   { geojson: {...} }
 *
 * - Objetos con Shape o shape (WKT)
 *   { Shape: "POLYGON(...)" }
 *
 * - FeatureCollection
 *
 * - Feature
 *
 * - Array de Features
 *
 * - Geometrías GeoJSON
 *   Polygon, MultiPolygon, Point, MultiPoint,
 *   LineString, MultiLineString, etc.
 *
 * Comportamiento:
 *
 * - Siempre retorna un GeoJSON FeatureCollection.
 * - Las geometrías sueltas se envuelven en un Feature.
 * - Los objetos WKT son convertidos mediante polygonToGeoJSON.
 * - Las propiedades suministradas pueden fusionarse con las
 *   existentes de cada Feature.
 *
 * @param {*} input
 * Entrada geoespacial a normalizar.
 *
 * @param {object} [options]
 *
 * @param {object} [options.properties={}]
 * Propiedades adicionales a aplicar a los Features.
 *
 * @param {boolean} [options.isUTM=true]
 * Indica si las coordenadas WKT están en UTM y deben
 * transformarse a WGS84.
 *
 * @param {boolean} [options.mergeProperties=true]
 * Si es true, fusiona las propiedades recibidas con las
 * propiedades existentes del Feature.
 * Si es false, reemplaza completamente las propiedades.
 *
 * @returns {GeoJSON.FeatureCollection|null}
 * Retorna un FeatureCollection válido o null si la entrada
 * no puede convertirse.
 */
export function normalizeGeoInput(
  input,
  { properties = {}, isUTM = true, mergeProperties = true } = {},
) {
  if (!input) return null;

  // -------------------------------------------------------
  // GeoJSON string
  // -------------------------------------------------------
  if (typeof input === "string") {
    try {
      input = JSON.parse(input);
    } catch {
      return null;
    }
  }

  // -------------------------------------------------------
  // Leaflet GeoJSON Layer
  // -------------------------------------------------------
  const isLeafletLayer =
    input &&
    typeof input.toGeoJSON === "function" &&
    typeof input.getBounds === "function";

  if (isLeafletLayer) {
    input = input.toGeoJSON();
  }

  // -------------------------------------------------------
  // Helper merge properties
  // -------------------------------------------------------
  const mergeProps = (feature) => {
    if (!mergeProperties) {
      return {
        ...feature,
        properties,
      };
    }

    const currentProps = feature.properties || {};

    return {
      ...feature,
      properties: {
        ...properties,
        ...currentProps, // las propiedades del Feature tienen prioridad
      },
    };
  };

  // -------------------------------------------------------
  // Wrapper { geojson: ... }
  // -------------------------------------------------------
  if (input?.geojson) {
    return normalizeGeoInput(input.geojson, {
      properties,
      isUTM,
      mergeProperties,
    });
  }

  // -------------------------------------------------------
  // Shape / WKT legacy
  // -------------------------------------------------------
  const shape = input?.Shape ?? input?.shape;

  if (typeof shape === "string") {
    return polygonToGeoJSON(shape, {
      isUTM,
      properties,
    });
  }

  // -------------------------------------------------------
  // FeatureCollection
  // -------------------------------------------------------
  if (
    input?.type === "FeatureCollection" &&
    Array.isArray(input.features)
  ) {
    if (!mergeProperties) {
      return input;
    }

    return {
      ...input,
      features: input.features.map(mergeProps),
    };
  }

  // -------------------------------------------------------
  // Feature
  // -------------------------------------------------------
  if (input?.type === "Feature") {
    return {
      type: "FeatureCollection",
      features: [mergeProps(input)],
    };
  }

  // -------------------------------------------------------
  // Array<Feature>
  // -------------------------------------------------------
  if (
    Array.isArray(input) &&
    input.every((f) => f?.type === "Feature")
  ) {
    return {
      type: "FeatureCollection",
      features: input.map(mergeProps),
    };
  }

  // -------------------------------------------------------
  // Geometry
  // -------------------------------------------------------
  if (input?.type && input?.coordinates) {
    return {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: input,
          properties,
        },
      ],
    };
  }

  return null;
}